/**
 * Geometry for panels anchored to a trigger element (select menus, dropdowns).
 *
 * Panels that teleport to <body> cannot be positioned with `absolute` +
 * `window.pageYOffset`: the admin shell scrolls an inner `overflow-y-auto`
 * section, so the document never scrolls and that offset is always 0. These
 * helpers work in viewport coordinates instead, which are correct no matter
 * which ancestor does the scrolling.
 *
 * Two viewports matter. The *layout* viewport (`window.innerWidth/innerHeight`)
 * is what `position: fixed` offsets and `getBoundingClientRect()` are measured
 * against. The *visual* viewport (`window.visualViewport`) is the part of it the
 * user can actually see: the soft keyboard and pinch-zoom shrink it without
 * touching the layout viewport. Panels are sized against the visual viewport,
 * because that is the room that exists; a trigger's visibility is judged against
 * the layout viewport, because being covered by the keyboard is not the same as
 * having scrolled away.
 *
 * Kept free of Vue so both the Composition API composable
 * (`useAnchoredDropdown`) and the legacy Options API `Select.vue` can share —
 * and so the geometry is unit-testable without mounting a component.
 */

export interface Rect {
  top: number;
  right: number;
  bottom: number;
  left: number;
  width: number;
  height: number;
}

export type Placement = "bottom" | "top";

export interface AnchoredPositionStyle {
  position: "fixed";
  left: string;
  top: string;
  bottom: string;
  width: string;
  maxHeight: string;
}

export interface AnchoredPosition {
  placement: Placement;
  maxHeight: number;
  /** False when the trigger has scrolled out of its clipping ancestor. */
  anchorVisible: boolean;
  style: AnchoredPositionStyle;
}

export interface AnchoredPositionInput {
  anchorRect: Rect;
  /** Bounds the panel must fit inside — what placement and max-height are sized against. */
  clipRect: Rect;
  /**
   * Bounds the *trigger* is visible within, used only for `anchorVisible`.
   * Separate from clipRect because a panel teleported to <body> escapes the
   * trigger's clipping ancestors while the trigger itself does not.
   * Defaults to clipRect.
   */
  visibilityRect?: Rect;
  /** Gap between trigger and panel. */
  offset?: number;
  /** Height the panel wants; the preferred side is kept if it can fit this. */
  preferredHeight?: number;
  /** Keep the panel this far inside the clipping edges. */
  edgeMargin?: number;
  /** Panel width. Defaults to the anchor's width (the select-menu convention). */
  width?: number;
  /**
   * Clamp the panel's near edge to clipRect when the anchor lies outside it (a
   * trigger the soft keyboard covers). Only a fixed panel can leave its anchor;
   * an in-flow one is CSS-attached, so its room is measured from the anchor's
   * edge.
   */
  shift?: boolean;
  /**
   * The side the panel is on now. Given, the side is kept while it stays
   * usable and changes only when the preferred side fits again or the current
   * side cannot show `minUsableHeight` and the other offers meaningfully more.
   * Omit on open so the side is decided afresh.
   */
  currentPlacement?: Placement;
  /** Room a kept side must still offer before a switch is considered. */
  minUsableHeight?: number;
}

/**
 * How much more room the other side must offer before an open panel switches
 * to it. A keyboard animation or a pan re-measures on every frame, and around
 * a near tie a memoryless rule would flip the panel on each one.
 */
const PLACEMENT_FLIP_MARGIN = 40;

const OVERFLOW_CLIPS = new Set(["auto", "scroll", "hidden", "clip", "overlay"]);

const toRect = (r: DOMRect | Rect): Rect => ({
  top: r.top,
  right: r.right,
  bottom: r.bottom,
  left: r.left,
  width: r.width,
  height: r.height
});

const intersect = (a: Rect, b: Rect): Rect => {
  const top = Math.max(a.top, b.top);
  const left = Math.max(a.left, b.left);
  const right = Math.min(a.right, b.right);
  const bottom = Math.min(a.bottom, b.bottom);
  return {
    top,
    left,
    right,
    bottom,
    width: right - left,
    height: bottom - top
  };
};

const layoutViewportRect = (): Rect => {
  const width = window.innerWidth;
  const height = window.innerHeight;
  return { top: 0, left: 0, right: width, bottom: height, width, height };
};

/** The visible part of the layout viewport, in layout coordinates. */
export const visualViewportRect = (): Rect => {
  const visual = window.visualViewport;
  if (!visual) return layoutViewportRect();

  const { offsetTop: top, offsetLeft: left, width, height } = visual;
  return {
    top,
    left,
    right: left + width,
    bottom: top + height,
    width,
    height
  };
};

/**
 * The region the anchor is actually visible within: the layout viewport
 * intersected with every scrolling/clipping ancestor. Always the basis for
 * judging whether the trigger is still on screen; only the basis for sizing the
 * panel when the panel shares the anchor's clipping ancestors (see
 * `escapesClipping`). Layout viewport on purpose: a trigger under the keyboard
 * has not scrolled away.
 */
export const clippingRectFor = (el: Element | null): Rect => {
  let rect = layoutViewportRect();
  if (!el) return rect;

  let node = el.parentElement;
  while (node && node !== document.body && node !== document.documentElement) {
    const { overflowX, overflowY } = window.getComputedStyle(node);
    if (OVERFLOW_CLIPS.has(overflowY) || OVERFLOW_CLIPS.has(overflowX)) {
      rect = intersect(rect, toRect(node.getBoundingClientRect()));
    }
    node = node.parentElement;
  }
  return rect;
};

/**
 * Which side an open panel should be on, given the room on each side.
 *
 * On open (no `current`): below when the preferred height fits, otherwise the
 * roomier side. While open the side has memory: it returns below as soon as
 * below fits the preferred height again (keyboard down, scrolled back), holds
 * while it can still show a usable height, and otherwise moves only to a side
 * that is better by more than the flip margin. Two cramped sides trading more
 * than the margin per frame can still alternate; a monotonic change settles
 * after at most one switch.
 */
const decidePlacement = (
  room: Record<Placement, number>,
  preferredHeight: number,
  minUsableHeight: number,
  current?: Placement
): Placement => {
  if (!current) {
    return room.bottom >= preferredHeight || room.bottom >= room.top
      ? "bottom"
      : "top";
  }
  if (current === "top" && room.bottom >= preferredHeight) return "bottom";
  if (room[current] >= Math.min(preferredHeight, minUsableHeight)) {
    return current;
  }
  const other: Placement = current === "bottom" ? "top" : "bottom";
  return room[other] >= room[current] + PLACEMENT_FLIP_MARGIN ? other : current;
};

/**
 * Place the panel below the anchor when it fits, otherwise on whichever side
 * has more room, and cap its height to that room so it is never cut off by the
 * clip — not by the viewport edge, and not by the soft keyboard. Pass
 * `currentPlacement` while the panel is open so the side has memory; see
 * `decidePlacement`.
 *
 * Top placement is expressed as a `bottom` offset rather than `top` + measured
 * height: the panel then grows upward from the trigger without us needing to
 * know how tall its content is.
 */
export const computeAnchoredPosition = ({
  anchorRect,
  clipRect,
  visibilityRect,
  offset = 1,
  preferredHeight = 320,
  edgeMargin = 8,
  width,
  shift = false,
  currentPlacement,
  minUsableHeight = 120
}: AnchoredPositionInput): AnchoredPosition => {
  const visible = visibilityRect ?? clipRect;
  // A zero-size rect means the anchor has not been laid out (or is display:none
  // in a test environment) — that tells us nothing about visibility, so don't
  // report it as scrolled away and have the caller dismiss the panel.
  const anchorHasLayout = anchorRect.width > 0 || anchorRect.height > 0;
  const anchorVisible =
    !anchorHasLayout ||
    (anchorRect.bottom > visible.top &&
      anchorRect.top < visible.bottom &&
      anchorRect.right > visible.left &&
      anchorRect.left < visible.right);

  const belowEdge = anchorRect.bottom + offset;
  const aboveEdge = anchorRect.top - offset;
  const panelTop = shift ? Math.max(belowEdge, clipRect.top) : belowEdge;
  const panelBottom = shift ? Math.min(aboveEdge, clipRect.bottom) : aboveEdge;

  const spaceBelow = clipRect.bottom - edgeMargin - panelTop;
  const spaceAbove = panelBottom - clipRect.top - edgeMargin;

  const placement = decidePlacement(
    { bottom: spaceBelow, top: spaceAbove },
    preferredHeight,
    minUsableHeight,
    currentPlacement
  );

  // Never taller than the room measured: a floor here would only put the
  // excess behind the keyboard or past the clip edge, where nobody can use it.
  const maxHeight = Math.max(
    0,
    Math.min(placement === "bottom" ? spaceBelow : spaceAbove, preferredHeight)
  );

  const panelWidth = width ?? anchorRect.width;
  const maxLeft = clipRect.right - panelWidth - edgeMargin;
  const minLeft = clipRect.left + edgeMargin;
  // When the panel is wider than the space available, pin to the left edge
  // rather than letting the clamp push it off the other side.
  const left =
    maxLeft < minLeft
      ? minLeft
      : Math.min(Math.max(anchorRect.left, minLeft), maxLeft);

  return {
    placement,
    maxHeight,
    anchorVisible,
    style: {
      position: "fixed",
      left: `${Math.round(left)}px`,
      // The unused axis must be reset explicitly: leaving a stale `top` from a
      // previous placement (or a stylesheet's `top`) alongside `bottom` would
      // stretch the panel between the two.
      top: placement === "bottom" ? `${Math.round(panelTop)}px` : "auto",
      bottom:
        placement === "top"
          ? `${Math.round(window.innerHeight - panelBottom)}px`
          : "auto",
      width: `${Math.round(panelWidth)}px`,
      maxHeight: `${Math.round(maxHeight)}px`
    }
  };
};

interface PositionForAnchorOptions extends Omit<
  AnchoredPositionInput,
  "anchorRect" | "clipRect" | "visibilityRect" | "shift"
> {
  /**
   * True when the panel is teleported out of the anchor's subtree. Such a panel
   * is not clipped by the anchor's scrolling ancestors, so sizing it against
   * them would flip or shrink it for room it actually has — only the visible
   * viewport constrains it, and it is free to shift along that viewport's edge
   * when the trigger is covered. The anchor's own visibility is still judged
   * against those ancestors either way.
   */
  escapesClipping?: boolean;
}

/** Convenience wrapper that reads the anchor's live geometry. */
export const positionForAnchor = (
  anchor: Element,
  { escapesClipping = false, ...options }: PositionForAnchorOptions = {}
): AnchoredPosition => {
  const visibilityRect = clippingRectFor(anchor);
  const visible = visualViewportRect();
  return computeAnchoredPosition({
    anchorRect: toRect(anchor.getBoundingClientRect()),
    clipRect: escapesClipping ? visible : intersect(visible, visibilityRect),
    visibilityRect,
    shift: escapesClipping,
    ...options
  });
};

/**
 * Call `onChange` whenever something that can move an anchored panel changes:
 * any ancestor scrolling, the window or the visible viewport resizing or
 * panning, or the anchor itself resizing. Returns the function that stops
 * tracking.
 *
 * `scroll` is listened for in the capture phase because scroll events don't
 * bubble: the admin shell scrolls an inner `overflow-y-auto` section, and
 * capturing on `window` is the only way to hear about that without knowing
 * which ancestor it is. The visual viewport fires its own events — the soft
 * keyboard and pinch-zoom change the visible area without any `window` event.
 *
 * Bursts are collapsed to one call per animation frame: scroll fires far
 * faster than a panel can usefully be repositioned, and rotation and the
 * keyboard each fire several of these events at once.
 */
export const trackAnchor = (
  anchor: Element,
  onChange: () => void
): (() => void) => {
  let frame: number | null = null;
  const schedule = () => {
    if (frame !== null) return;
    frame = requestAnimationFrame(() => {
      frame = null;
      onChange();
    });
  };

  window.addEventListener("scroll", schedule, { capture: true, passive: true });
  window.addEventListener("resize", schedule);
  window.visualViewport?.addEventListener("resize", schedule);
  window.visualViewport?.addEventListener("scroll", schedule, {
    passive: true
  });
  const observer =
    typeof ResizeObserver === "undefined" ? null : new ResizeObserver(schedule);
  observer?.observe(anchor);

  return () => {
    window.removeEventListener("scroll", schedule, { capture: true });
    window.removeEventListener("resize", schedule);
    window.visualViewport?.removeEventListener("resize", schedule);
    window.visualViewport?.removeEventListener("scroll", schedule);
    observer?.disconnect();
    if (frame !== null) cancelAnimationFrame(frame);
    frame = null;
  };
};
